import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Database,
  Settings2,
  Cookie,
  Share2,
  Lock,
  Clock,
  UserCheck,
  Link2,
  Baby,
  RefreshCcw,
  ArrowRight,
} from "lucide-react";

import { RevealSection, MagneticButton } from "../components/motion/ScrollFX";
import {
  legalTheme as theme,
  DotGrid,
  ScrollProgressRail,
  useActiveSection,
  LegalHero,
  SectionNav,
  MobileSectionPills,
  LegalSection,
  ContactSection,
} from "../components/legal/LegalKit";

const sections = [
  {
    id: "collection-of-your-information",
    no: 1,
    icon: Database,
    title: "Collection of Your Information",
    paragraphs: [
      "We collect personal data you provide directly to us — such as your name, contact details, company or organisation name, and any information submitted through contact forms, enquiries, or job applications (including your resume or CV) — as well as data collected automatically, such as your IP address, device information, browsing behaviour, and cookies.",
      "We may also collect data from third-party services such as social media platforms, payment providers, and business partners where relevant.",
    ],
  },
  {
    id: "use-of-your-information",
    no: 2,
    icon: Settings2,
    title: "Use of Your Information",
    paragraphs: [
      "Your data is used to provide, maintain, and improve our services and website, respond to your enquiries, process job applications, detect fraud, and personalise your experience.",
      "You may opt out of marketing communications at any time.",
    ],
  },
  {
    id: "sharing-of-information",
    no: 3,
    icon: Share2,
    title: "Sharing of Information",
    paragraphs: [
      "We may share data with trusted service providers, cloud hosting and IT support providers, payment processors, and other third parties assisting us in providing our services.",
      "Third parties are required to maintain confidentiality and comply with applicable privacy laws. We do not sell your personal data.",
    ],
  },
  {
    id: "cookies-and-tracking",
    no: 4,
    icon: Cookie,
    title: "Cookies & Tracking",
    paragraphs: [
      "We use cookies, web beacons, and similar tracking technologies to improve your browsing experience, understand usage patterns, analyse trends, and deliver relevant content.",
    ],
    note: "You can manage your cookie preferences through your browser settings.",
  },
  {
    id: "data-retention",
    no: 5,
    icon: Clock,
    title: "Data Retention",
    paragraphs: [
      "Personal data is retained only for as long as necessary to provide our services, meet legal obligations, resolve disputes, or enforce our agreements.",
    ],
    note: "Data that is no longer required is securely deleted or anonymised.",
  },
  {
    id: "security-of-your-information",
    no: 6,
    icon: Lock,
    title: "Security of Your Information",
    paragraphs: [
      "We use industry-standard security practices, including encryption, secure servers, and access controls, to safeguard your information.",
    ],
    note: "No method of transmission or electronic storage can be guaranteed to be completely secure.",
  },
  {
    id: "your-rights",
    no: 7,
    icon: UserCheck,
    title: "Your Rights",
    paragraphs: [
      "Subject to applicable laws, you have the right to access, correct, update, or request deletion of your personal data. You may also withdraw consent where our processing is based on consent.",
    ],
    note: "To exercise these rights, please contact us using the details below.",
  },
  {
    id: "third-party-links",
    no: 8,
    icon: Link2,
    title: "Third-Party Links",
    paragraphs: [
      "Our website may include links to third-party websites or services. We are not responsible for the content or privacy practices of those third parties, and we encourage you to review their respective privacy policies.",
    ],
  },
  {
    id: "childrens-privacy",
    no: 9,
    icon: Baby,
    title: "Children's Privacy",
    paragraphs: [
      "Our website is not intended for individuals under the age of 18 without the supervision of a parent or guardian.",
      "We do not knowingly collect personal information from children. If such information is identified, appropriate steps will be taken to remove it.",
    ],
  },
  {
    id: "changes-to-this-privacy-policy",
    no: 10,
    icon: RefreshCcw,
    title: "Changes to This Privacy Policy",
    paragraphs: [
      'This Privacy Policy may be updated periodically to reflect changes in our services, legal requirements, or business practices. The updated version will be posted on this page with a revised "Last Updated" date.',
      "Continued use of our website after such updates constitutes acceptance of the revised policy.",
    ],
  },
];

const contactCards = [
  { label: "General Enquiries", desc: "Questions about this policy or how we handle your information.", email: "support@unibcomp.net" },
  { label: "Grievance Officer", desc: "For privacy-related concerns or formal grievances.", email: "grievance@unibcomp.in" },
];

export default function PrivacyPolicy() {
  const activeId = useActiveSection([...sections.map((s) => s.id), "contact-us"]);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <ScrollProgressRail />

      <LegalHero
        eyebrow="Legal"
        title={["Privacy", "Policy"]}
        HeroIcon={ShieldCheck}
        meta={
          <>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck size={15} style={{ color: theme.gold }} />
              Last Updated: July 2026
            </span>
            <span className="hidden sm:inline">·</span>
            <span>UniBcomp Networks Private Limited</span>
          </>
        }
        description='We value your privacy. This Privacy Policy explains how UniBcomp Networks Private Limited ("UniBcomp", "we", "our", or "us") collects, uses, shares, and processes your personal data through our website and related services.'
        agreementNote="This policy applies to all visitors, clients, and job applicants using our website at unibcomp.net. For privacy-related questions or requests, please contact us at support@unibcomp.net."
      />

      <MobileSectionPills sections={sections} activeId={activeId} />

      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[240px_1fr] gap-16">
          <SectionNav sections={sections} activeId={activeId} />

          <div className="min-w-0 max-w-3xl">
            {sections.map((section) => (
              <LegalSection key={section.id} section={section} />
            ))}

            <ContactSection
              no={11}
              intro="If you have any questions regarding this Privacy Policy or wish to exercise your privacy rights, please contact us:"
              cards={contactCards}
            />

            <div className="mt-8 pt-8 border-t text-sm" style={{ borderColor: theme.border, color: theme.inkMuted }}>
              © 2026 UniBcomp Networks Private Limited. All rights reserved.
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 overflow-hidden" style={{ backgroundColor: theme.darkBandDeep }}>
        <DotGrid light />
        <RevealSection className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white">Questions about how we handle your data?</h2>
          <p className="mt-3 text-white/50">Our grievance officer is happy to walk through anything in this policy.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
              >
                Contact Us
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}